import React, { useState, useEffect } from "react";
import { Drawer, message, Image, Spin, Button, Space, Input } from "antd";
import DOMPurify from "dompurify";
import { serverFetch, useGetIsPhone } from "@extensions";
import { useUser } from "@redux";
import { Auth } from "@components";
import CatalogSaleDates from "./catalog-sale-dates";
import CatalogSaleUsers from "./catalog-sale-users";
import CatalogSaleRooms from "./catalog-sale-rooms";
import CatalogSaleTime from "./catalog-sale-time";
import CatalogSaleGroups from "./catalog-sale-groups";
import "./catalog-sale.css";

const CatalogSale = ({ item, open, onClose, onPaymentOpen, onPurchaseSuccess, initialGroupRecord = null, mode = "drawer" }) => {
  const isPhone = useGetIsPhone();
  const user = useUser();
  const [product, setProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedGroupRecord, setSelectedGroupRecord] = useState(null);
  const [loadingPurchase, setLoadingPurchase] = useState(false);
  const [showSignin, setShowSignin] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState(false);
  const [giftData, setGiftData] = useState({
    email: "",
    name: "",
    message: "",
    sign: "",
  });
  
  const isEmailValid = (email) => /^\S+@\S+\.\S+$/.test((email || "").trim());

  useEffect(() => {
    if (open && item?.productId) {
      setSelectedSession(null);
      setSelectedUser(null);
      setSelectedRoom(null);
      setSelectedTime(null);
      setSelectedGroupRecord(initialGroupRecord);
      setLoadingProduct(true);
      serverFetch(`Online/Product/${item.productId}`)
        .then((data) => {
          setProduct(data);
          setLoadingProduct(false);
        })
        .catch((error) => {
          message.error(error?.UserMessage || "Не удалось загрузить информацию о продукте");
          setLoadingProduct(false);
        });
    } else {
      setProduct(null);
      setSelectedSession(null);
      setSelectedUser(null);
      setSelectedRoom(null);
      setSelectedTime(null);
      setSelectedGroupRecord(null);
      setGiftData({
        email: "",
        name: "",
        message: "",
        sign: "",
      });
    }
  }, [open, item?.productId, initialGroupRecord]);

  useEffect(() => {
    if (!open || product?.vid !== 4 || !user) {
      return;
    }

    let isMounted = true;
    serverFetch("OnlineClient/info", { method: "GET" })
      .then((client) => {
        if (!isMounted) { return; }
        setGiftData((prev) => ({
          email: prev.email || client?.email || "",
          name: prev.name || client?.name || "",
          message: prev.message || "",
          sign: prev.sign || "",
        }));
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [open, product?.vid, user]);

  // Продолжение покупки после авторизации
  useEffect(() => {
    if (pendingPurchase && user) {
      setPendingPurchase(false);
      setShowSignin(false);
      // Вызываем performPurchase через небольшую задержку, чтобы убедиться, что user обновлен
      setTimeout(() => {
        performPurchase();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPurchase, user]);

  const performPurchase = async () => {
    if (!item || !user) { return; }

    if (product?.recordVid === 1 && (!selectedSession?.date || !selectedUser || !selectedTime)) {
      message.error("Необходимо выбрать дату, сотрудника и время");
      return;
    }
    if (product?.recordVid === 2 && (!selectedSession?.date || !selectedRoom || !selectedTime)) {
      message.error("Необходимо выбрать дату, зал и время");
      return;
    }
    if (product?.recordVid === 3 && !selectedGroupRecord) {
      message.error("Необходимо выбрать группу");
      return;
    }
    if (product?.vid === 4 && !giftData.email.trim()) {
      message.error("Поле \"Почта\" обязательно для заполнения");
      return;
    }
    if (product?.vid === 4 && !isEmailValid(giftData.email)) {
      message.error("Введите корректный email");
      return;
    }

    setLoadingPurchase(true);

    try {
      const requestData = { catalogItemId: item.catalogId || item.id, };
      if (product?.recordVid === 1 || product?.recordVid === 2) {
        const dateStr = selectedSession.date.format("YYYY-MM-DD");
        const dateTimeStr = `${dateStr}T00:00:00`;
        requestData.date = dateTimeStr;
        let timeStr = selectedTime;
        if (timeStr && timeStr.length === 5) {
          timeStr = `${timeStr}:00`;
        } else if (timeStr && !timeStr.includes(":")) {
          timeStr = `${timeStr.padStart(2, "0")}:00:00`;
        }
        requestData.time = timeStr;
        if (product?.recordVid === 1) {
          requestData.userId = selectedUser.id;
        } else {
          requestData.roomId = selectedRoom.id;
        }
      }
      if (product?.recordVid === 3) {
        requestData.recordId = selectedGroupRecord.id;
      }
      if (product?.vid === 4) {
        requestData.email = giftData.email.trim();
        requestData.name = giftData.name.trim();
        requestData.message = giftData.message.trim();
        requestData.sign = giftData.sign.trim();
      }

      const result = await serverFetch(
        "OnlineClient/Sale",
        {
          method: "POST",
          bodyData: requestData,
        },
        user
      );

      if (result?.sale) {
        message.success("Покупка успешно оформлена!");
        if (onPurchaseSuccess) {
          onPurchaseSuccess(result.sale);
          return;
        }
        onClose();
        if (onPaymentOpen) {
          onPaymentOpen([result.sale]);
        }
      } else if (result?.record) {
        message.success("Вы успешно записаны!");
        onClose();
      } else {
        message.error("Не удалось оформить покупку");
      }
    } catch (error) {
      console.error("Ошибка при оформлении покупки:", error);
      message.error(error?.userMessage || error?.message || "Не удалось оформить покупку");
    } finally {
      setLoadingPurchase(false);
    }
  };

  const handlePurchase = () => {
    if (!item) {
      return;
    }

    if (!user) {
      setShowSignin(true);
      setPendingPurchase(true);
      return;
    }

    performPurchase();
  };

  const handleDateSelect = (session) => {
    setSelectedSession(session);
    setSelectedUser(null);
    setSelectedRoom(null);
    setSelectedTime(null);
  };

  const handleUserSelect = (selected) => {
    setSelectedUser(selected);
    setSelectedTime(null);
  };

  const handleRoomSelect = (selected) => {
    setSelectedRoom(selected);
    setSelectedTime(null);
  };

  const saleContent = (
    <>
      {loadingProduct ? (
        <div className="catalog-sale-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {product?.pictureId && (
            <div className="catalog-sale-image-container">
              <Image
                src={`${process.env.REACT_APP_API_BASE_URL}/api/image/${product.pictureId}`}
                alt={item?.name}
                preview={false}
                className="catalog-sale-image"
              />
            </div>
          )}
          {product?.description && (
            <div className="catalog-sale-description">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(product.description, {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6"],
                  }),
                }}
              />
            </div>
          )}
          {item?.price && (
            <div className="catalog-sale-price">{item.price.toLocaleString()} руб.</div>
          )}
          {product?.recordVid === 1 && (
            <>
              <CatalogSaleDates onSelect={handleDateSelect} />
              {selectedSession?.date && (
                <CatalogSaleUsers
                  productId={item?.productId}
                  selectedDate={selectedSession.date}
                  onSelect={handleUserSelect}
                />
              )}
              {selectedUser && selectedSession?.date && (
                <CatalogSaleTime
                  userId={selectedUser.id}
                  productId={item?.productId}
                  selectedDate={selectedSession.date}
                  onSelect={setSelectedTime}
                />
              )}
            </>
          )}
          {product?.recordVid === 2 && (
            <>
              <CatalogSaleDates onSelect={handleDateSelect} />
              {selectedSession?.date && (
                <CatalogSaleRooms
                  productId={item?.productId}
                  selectedDate={selectedSession.date}
                  onSelect={handleRoomSelect}
                />
              )}
              {selectedRoom && selectedSession?.date && (
                <CatalogSaleTime
                  roomId={selectedRoom.id}
                  productId={item?.productId}
                  selectedDate={selectedSession.date}
                  onSelect={setSelectedTime}
                />
              )}
            </>
          )}
          {product?.recordVid === 3 && (
            <CatalogSaleGroups
              productId={item?.productId}
              onSelect={setSelectedGroupRecord}
              initialGroupRecord={initialGroupRecord}
            />
          )}
          {product?.vid === 4 && (
            <div className="catalog-sale-gift-fields">
              <Input
                placeholder="Почта"
                value={giftData.email}
                onChange={(e) => setGiftData((prev) => ({ ...prev, email: e.target.value }))}
                status={!giftData.email.trim() || isEmailValid(giftData.email) ? "" : "error"}
                style={{ marginBottom: 8 }}
              />
              <Input
                placeholder="Получатель"
                value={giftData.name}
                onChange={(e) => setGiftData((prev) => ({ ...prev, name: e.target.value }))}
                style={{ marginBottom: 8 }}
              />
              <Input.TextArea
                placeholder="Сообщение"
                value={giftData.message}
                onChange={(e) => setGiftData((prev) => ({ ...prev, message: e.target.value }))}
                autoSize={{ minRows: 3, maxRows: 6 }}
                style={{ marginBottom: 8 }}
              />
              <Input
                placeholder="Подпись"
                value={giftData.sign}
                onChange={(e) => setGiftData((prev) => ({ ...prev, sign: e.target.value }))}
                style={{ marginBottom: 8 }}
              />
            </div>
          )}
          <Space className="catalog-sale-buttons">
            <Button onClick={onClose} className="catalog-sale-button">Назад</Button>
            <Button
              className="catalog-sale-button"
              type="primary"
              disabled={
                loadingPurchase
                || (product?.recordVid === 1 && (!selectedSession?.date || !selectedUser || !selectedTime))
                || (product?.recordVid === 2 && (!selectedSession?.date || !selectedRoom || !selectedTime))
                || (product?.recordVid === 3 && !selectedGroupRecord)
                || (product?.vid === 4 && !giftData.email.trim())
              }
              loading={loadingPurchase}
              onClick={handlePurchase}
            >Купить</Button>
          </Space>
        </>
      )}
      {showSignin && <Auth open={true} onClose={() => { setShowSignin(false); setPendingPurchase(false); }} />}
    </>
  );

  if (mode === "page") {
    return (
      <div className={`catalog-sale-page-card ${product?.pictureId ? "" : "catalog-sale-page-card_no-image"}`}>
        <div className="catalog-sale-page-title">{item?.name}</div>
        {saleContent}
      </div>
    );
  }

  return (
    <Drawer
      title={item?.name}
      placement="right"
      onClose={onClose}
      open={open}
      width={isPhone ? "100%" : 540}
      closable={true}
      className="catalog-sale-drawer"
      styles={{ body: { padding: isPhone ? "8px 8px 8px 8px" : "0px 16px 16px 16px", paddingTop: 0 } }}
    >
      {saleContent}
    </Drawer>
  );
};

export default CatalogSale;
